import { useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { Tag, TagVariant } from '@deliveryhero/pd-cookbook/components/Tag';
import { useConfiguration } from 'contexts/country-config';
import intervalToDuration from 'date-fns/intervalToDuration';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import { OrderStatus, ReportFormat, ReportOrder } from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import { useFeatureFlags } from 'src/contexts/feature-flags';
import { FeatureFlag } from 'src/utils/constants';
import generateQuery from 'utils/generate-query';

import CTA from 'components/CallToActions';
import DateTimeCell from 'components/DateTimeCell';
import { MenuButton } from 'components/MenuButton';
import PageHeader from 'components/PageHeader/PageHeader';
import TransactionRefund from 'components/svg/TransactionRefund';
import TableList from 'components/TableList';
import { Column } from 'components/TableList/SortableHeader';
import { DefaultRowsPersPage } from 'components/TableList/types';

import { LabelText, TestId } from './constants';
import { ReportFilters } from './ReportFilters';

export function ReportContainer() {
  const api = useApi();
  const countryConfig = useConfiguration();
  const navigate = useNavigate();
  const queryParam = useQueryParam();
  const hasFeature = useFeatureFlags();
  const { pushNotification, dismissNotification } = useNotification();

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.REPORT });
  }, []);

  const page = queryParam.offset ? parseInt(queryParam.offset) : undefined;
  const rowsPerPage = queryParam.count ? parseInt(queryParam.count) : undefined;
  const tableListProps = {
    ...(page && {
      page: Math.floor(
        page / (rowsPerPage !== undefined ? rowsPerPage : DefaultRowsPersPage)
      )
    }),
    ...(rowsPerPage && { rowsPerPage })
  };
  const filters = [
    'search',
    'count',
    'offset',
    'created_from',
    'created_to',
    'format'
  ];

  const filtered = Object.keys(queryParam)
    .filter((key) => filters.includes(key))
    .reduce((param: Record<string, string>, key) => {
      param[key] = queryParam[key];
      return param;
    }, {});

  const query = useQueryWrapperP0(['report', { ...filtered }], () =>
    api.company.report.list(filtered)
  );

  const renderTag = (variant: TagVariant, label: string) => {
    return <Tag id={variant} label={label} variant={variant} />;
  };

  const columns: Column<ReportOrder>[] = useMemo(
    () => [
      {
        id: 'payment_type',
        options: { disablePadding: false },
        label: 'Order Type'
      },
      {
        id: 'code',
        options: { disablePadding: false },
        label: 'Order No.'
      },
      {
        id: 'user_name',
        options: { disablePadding: false },
        label: 'Name and Email',
        format: (report) => {
          return (
            <>
              <div>{report.user_name}</div>
              <span
                style={{
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: '#767676'
                }}
              >
                {report.email}
              </span>
            </>
          );
        }
      },
      {
        id: 'created_at',
        options: { disablePadding: false },
        label: 'Order Time',
        format: (report) => {
          return (
            <DateTimeCell
              date={report.created_at}
              timezone={countryConfig.timezone}
            />
          );
        }
      },
      {
        id: 'delivery_time',
        options: { disablePadding: false },
        label: 'Delivery Time',
        format: (report) => {
          return (
            <DateTimeCell
              date={report.delivery_time}
              timezone={countryConfig.timezone}
            />
          );
        }
      },
      {
        id: 'collection_type',
        options: { disablePadding: false },
        label: 'Collection Type'
      },
      {
        id: 'total_value',
        options: { disablePadding: false },
        label: `Total Order Value (${countryConfig.currencyCode})`
      },
      {
        id: 'state',
        options: { disablePadding: false },
        label: 'Status',
        format: (report) => {
          return report.state === OrderStatus.delivered ||
            report.state === OrderStatus.completed
            ? renderTag(TagVariant.SUCCESS, report.state)
            : report.state === OrderStatus.cancelled
              ? renderTag(TagVariant.POPULAR, report.state)
              : renderTag(TagVariant.INFORMATION, report.state);
        }
      }
    ],
    [countryConfig.timezone]
  );

  const disableDownloadButton = () => {
    if (queryParam['created_from'] && queryParam['created_to']) {
      const { years, days } = intervalToDuration({
        start: new Date(queryParam['created_from']),
        end: new Date(queryParam['created_to'])
      });
      return !years && days !== undefined && days < 31 ? false : true;
    }
    return false;
  };

  const handlePageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParam,
      ...changes
    };

    params = Object.keys(params).reduce(
      (acc, key) => {
        params[key] !== undefined && (acc[key] = params[key]);
        return acc;
      },
      {} as Record<string, any>
    );

    navigate(
      {
        pathname: AppRoute.Report,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    handlePageChange({
      ...{
        offset: page ? page * rowsPerPage : undefined,
        count: rowsPerPage
      }
    });
  };

  const { mutate: downloadReport, isLoading: isDownloading } = useMutation(
    async (format: ReportFormat) => {
      return api.company.report.download(
        createSearchParams({
          ...filtered,
          format: format
        })
      );
    }
  );

  const handleDownloadClick = (format: ReportFormat) => {
    downloadReport(format, {
      onSuccess: (data: unknown) => {
        const url = window.URL.createObjectURL(new Blob([data as Blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${LabelText.DOWNLOAD_SUBJECT}.${format}`
        );
        document.body.appendChild(link);
        link.click();
        pushNotification(LabelText.SUCCESS_MESSAGE, {
          variant: 'success'
        });
      },
      onError: (e) => {
        const error = e as Error;
        const errorSnackbarId = pushNotification(error.message, {
          variant: 'error',
          onClick: () => dismissNotification(errorSnackbarId)
        });
      }
    });
  };

  const hideDownloadButton =
    !query.data ||
    query.data?.data.length == 0 || // hide when no data
    query.isLoading; // hide when query is loading

  const renderDownloadOption = (
    key: number,
    format: ReportFormat,
    testId: string,
    label: string
  ) => (
    <div
      key={key}
      onClick={() => handleDownloadClick(format)}
      data-testid={testId}
      role="button"
    >
      {label}
    </div>
  );

  const buttons = [];
  if (!hideDownloadButton) {
    const isDownloadButtonDisabled = disableDownloadButton();

    const downloadButtons = [
      renderDownloadOption(
        1,
        ReportFormat.csv,
        TestId.csv,
        LabelText.DOWNLOAD_CSV_BUTTON
      ),
      renderDownloadOption(
        2,
        ReportFormat.xlsx,
        TestId.xlsx,
        LabelText.DOWNLOAD_EXCEL_BUTTON
      )
    ];

    buttons.push(
      <MenuButton
        key="1"
        variant="primary"
        label={LabelText.DOWNLOAD_BUTTON}
        isDisabled={isDownloadButtonDisabled}
        displayOnlyWhenDisabled
        toolTipText={LabelText.TOOLTIP}
        isLoading={isDownloading}
        buttons={downloadButtons}
      />
    );
  }

  return (
    <>
      <PageHeader title={LabelText.TITLE} buttons={buttons}></PageHeader>
      <TableList<ReportOrder>
        total={query.error ? 0 : query.data ? query.data.total : -1}
        empty={
          Object.keys(filtered).length === 0 && (
            <CTA
              illustration={TransactionRefund}
              title={LabelText.EMPTY_PAGE_TITLE}
              subtitle={LabelText.EMPTY_PAGE_SUBTITLE}
            />
          )
        }
        columns={columns}
        data={query.data?.data || []}
        onPaginationChange={handlePaginationChange}
        expandFilters={true}
        loading={query.isLoading}
        {...tableListProps}
      >
        <ReportFilters params={queryParam} onPageChange={handlePageChange} />
      </TableList>
    </>
  );
}
